

exports.__esModule = true;
exports.roundingAndSeparators = roundingAndSeparators;
exports.formatCurrency = formatCurrency;

var _locale = require('./locale');

// This seems to be the only currency / locale specific exception in Griffin
var STYLE_EXCEPTION = {
    'tr TR TRY': '# ¤;-# ¤'
}; // everything to do with currency formatting

// quick Griffin reference on currency format:
// ¤ : Currency Symbol = $
// # : Amount = 186432.431
// C : Currency Code = USD

var ALL_NON_BREAKING_SPACE = new RegExp('\xa0+', 'g');
var CODE_IN_FRONT = new RegExp('^([^¤#]*)(C[\\s]*)(.*)$'); // matching groups: (any negative symbol)(code)(the rest)
var ENDS_IN_WHITESPACE = new RegExp('\s$');
var THOUSANDS_SPLITTER = /\B(?=(\d{3})+(?!\d))/g; // eslint-disable-line fasec/no-unsafe-regex

// Griffin "fuzzy logic"
// apparently if the symbol is at least "66% similar" to the code, ignore it
function maybeIgnoreSymbol(style, symbol, code) {

    // THIS IS NOT COMMUTATIVE
    function fuzzyMatch(code, symbol) {
        // eslint-disable-line no-shadow
        if (code === symbol) {
            return 100;
        } else {
            var codeParts = code.split('');
            var symbolParts = symbol.split('');

            var equivalency = codeParts.reduce(function (equiv, codePart) {
                var symbolPos = symbolParts.indexOf(codePart);

                if (symbolPos !== -1) {
                    symbolParts.splice(symbolPos, 1);
                    return equiv + 1;
                }
                return equiv;
            }, 0);

            var weight = equivalency / codeParts.length;

            return weight * 100;
        }
    }

    var ignoreSymbol = symbol.length > 1 && fuzzyMatch(code, symbol) >= 66;

    return ignoreSymbol ? style.replace(/¤\s*/g, '') : style;
}

// deal with rounding due to truncation (Griffin rounds up) as well as dot/comma symbols
// and thousand separators
function roundingAndSeparators(amountString, _ref) {
    var decimalPlaces = _ref.decimalPlaces,
        dot = _ref.dot,
        comma = _ref.comma;

    var value = parseFloat(amountString);
    var truncate = decimalPlaces === 0;

    var amountParts = truncate && !isNaN(value) ? Math.round(value).toString().split('.') : amountString.split('.');
    var wholeString = amountParts[0].replace(THOUSANDS_SPLITTER, comma);
    var decimalString = amountParts[1] || '00';

    return truncate ? wholeString : '' + wholeString + dot + decimalString;
}

// this is an agreement with UED for visual consistency - the currency code, if displayed, is always on the right
function forceCodeToRight(style) {
    var parts = CODE_IN_FRONT.exec(style) || [];
    var endsWithWhitespace = ENDS_IN_WHITESPACE.test(style);
    var newStyle = style;

    // if CODE_IN_FRONT regex matches, array indexes -
    // [1] negative symbol plus whitespace, if any,
    // [2] the "C" plus any whitespace,
    // [3] whatever is after "C"
    if (parts.length === 4) {
        newStyle = parts[1] + parts[3] + (endsWithWhitespace ? 'C' : ' C');
    }

    return newStyle;
}

// deal with
// 1. exceptions for specific locales
// 2. positive and negative styles
// 3. griffin fuzzy logic
// 4. UED consistency logic
function getFinalStyle() {
    var styleString = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : ';';
    var _ref2 = arguments[1];
    var lang = _ref2.lang,
        country = _ref2.country;
    var _ref3 = arguments[2];
    var value = _ref3.value,
        _ref3$symbol = _ref3.symbol,
        symbol = _ref3$symbol === undefined ? '' : _ref3$symbol,
        code = _ref3.code;


    var exception = STYLE_EXCEPTION[[lang, country, code].join(' ')]; // special exceptions
    var currencyStyle = exception || styleString;

    var _currencyStyle$split = currencyStyle.split(';'),
        positiveStyle = _currencyStyle$split[0],
        negativeStyle = _currencyStyle$split[1]; // the style is separated by postive and negative notation via a ;

    var baseStyle = value < 0 ? negativeStyle || '-¤# C' : positiveStyle || '¤# C';
    var griffinStyle = exception ? baseStyle : maybeIgnoreSymbol(baseStyle, symbol, code); // don't apply fuzzy logic on exceptions

    return forceCodeToRight(griffinStyle);
}

// take the style and the formatted amount (rounded / truncated / thousands separator / decimal separator)
// and produce the final result.
//
// wrapCodeInSpan - wraps the currency code in a span.currencyCode for styling purposes
function applyCurrencyStyle(finalStyle, amountFormatted, _ref4) {
    var symbol = _ref4.symbol,
        code = _ref4.code,
        _ref4$wrapCodeInSpan = _ref4.wrapCodeInSpan,
        wrapCodeInSpan = _ref4$wrapCodeInSpan === undefined ? false : _ref4$wrapCodeInSpan;

    // the style will take care of negative symbol (see getFinalStyle function above)
    var absoluteAmountFormatted = amountFormatted.replace('-', '');

    // parallel operation is safer than serial replace
    return finalStyle.split('').map(function (char) {
        switch (char) {
            case 'C':
                return wrapCodeInSpan ? '<span class="currencyCode">' + code + '</span>' : code;
            case '¤':
                return symbol;
            case '#':
                return absoluteAmountFormatted;
            default:
                return char;
        }
    }).join('').replace(ALL_NON_BREAKING_SPACE, ' ');
}

function formatCurrency(amount, code, wrapCodeInSpan) {
    var style = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 'SymbolISOCurrency';

    var _ref5 = _locale.$locale.metadata || {},
        _ref5$number = _ref5.number,
        numberConfig = _ref5$number === undefined ? {} : _ref5$number,
        _ref5$currency = _ref5.currency,
        currencyConfig = _ref5$currency === undefined ? {} : _ref5$currency;

    var _currencyConfig$digit = currencyConfig.digits,
        digits = _currencyConfig$digit === undefined ? {} : _currencyConfig$digit,
        _currencyConfig$style = currencyConfig.styles,
        styles = _currencyConfig$style === undefined ? {} : _currencyConfig$style,
        _currencyConfig$suppo = currencyConfig.supportedSymbols,
        currencySymbols = _currencyConfig$suppo === undefined ? {} : _currencyConfig$suppo;

    var value = parseFloat(amount);

    var dot = numberConfig.DECIMAL_SEP === undefined ? '.' : numberConfig.DECIMAL_SEP;
    var comma = numberConfig.GROUP_SEP === undefined ? ',' : numberConfig.GROUP_SEP;

    var amountFormatted = roundingAndSeparators(amount.toString(), { decimalPlaces: digits[code], dot: dot, comma: comma });

    if (code) {
        var symbol = currencySymbols[code];
        var finalStyle = getFinalStyle(styles[style], _locale.$locale, { value: value, symbol: symbol, code: code });

        return applyCurrencyStyle(finalStyle, amountFormatted, { symbol: symbol, code: code, wrapCodeInSpan: wrapCodeInSpan });
    }

    return amountFormatted; // HTML?
}

_locale.$locale;


//////////////////
// WEBPACK FOOTER
// ./components/squid/dist/locale/currency.js
// module id = ./components/squid/dist/locale/currency.js
// module chunks = 1